import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Container,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Toolbar,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import GroupsIcon from '@mui/icons-material/Groups';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import axiosInstance from '../api/axiosConfig';
import swimService from '../api/services/swimService';
import { ResponsiveTable, ResponsiveTableColumn } from '../components/common/ResponsiveTable';
import { useSnackbar } from '../components/common/SnackbarProvider';
import { useIsMobile } from '../hooks/useIsMobile';

interface ProgramLite {
  id: string;
  name: string;
  program_type: string;
  status: string;
  is_active: boolean;
  max_participants: number;
  start_date: string;
  end_date: string;
  target_audience?: 'kids' | 'adult' | 'all' | string;
}

interface EnrolleeLite {
  id?: string | number;
  type?: 'member' | 'non_member' | 'dependent' | 'non_member_dependent';
  first_name?: string;
  last_name?: string;
  email?: string;
  date_of_birth?: string | null;
}

// Backend types collapse into two display buckets — anything member-side
// (full member or their dependent) is "Member"; non-member family + their
// kids are "NonMember". Coaches just need the financial bucket, not the
// data-model nuance.
function enrolleeBucket(t?: string): 'Member' | 'NonMember' | '—' {
  if (!t) return '—';
  if (t === 'member' || t === 'dependent') return 'Member';
  if (t === 'non_member' || t === 'non_member_dependent') return 'NonMember';
  return '—';
}

function ageFromDob(dob?: string | null): number | null {
  if (!dob) return null;
  const d = new Date(dob);
  if (Number.isNaN(d.getTime())) return null;
  const t = new Date();
  let a = t.getFullYear() - d.getFullYear();
  const m = t.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && t.getDate() < d.getDate())) a -= 1;
  return a;
}

interface EnrollmentRow {
  id: string;
  status: 'registered' | 'waitlisted' | 'cancelled' | 'completed';
  payment_status: 'pending' | 'paid' | 'refunded' | 'waived';
  amount_due: number;
  amount_paid: number;
  enrolled_at: string | null;
  waitlist_position: number | null;
  notes: string | null;
  enrollee?: EnrolleeLite;
  // Populated when the row comes from the all-programs aggregated endpoint.
  program?: {
    id: string;
    name: string;
    program_type?: string;
    target_audience?: 'kids' | 'adult' | 'all' | string;
  };
}

const ALL_PROGRAMS = '__all__';

const statusColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  registered: 'success',
  waitlisted: 'warning',
  cancelled: 'default',
  completed: 'info',
};

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'registered', label: 'Registered' },
  { value: 'waitlisted', label: 'Waitlisted' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

export type EnrollmentScope = 'swim' | 'tennis' | 'all';

interface AdminProgramEnrollmentsPageProps {
  scope?: EnrollmentScope;
}

const SCOPE_LABEL: Record<EnrollmentScope, { title: string; subtitle: string; programTypes: string[] | null }> = {
  all: {
    title: 'All Program Enrollments',
    subtitle: 'Every program, every type — pick one to see who is enrolled.',
    programTypes: null,
  },
  swim: {
    title: 'Swim Enrollments',
    subtitle: 'Pick a swim program to see everyone enrolled across all statuses.',
    programTypes: ['swim'],
  },
  tennis: {
    title: 'Tennis Enrollments',
    subtitle: 'Pick a tennis or pickleball program to see everyone enrolled across all statuses.',
    programTypes: ['tennis', 'pickleball'],
  },
};

const AdminProgramEnrollmentsPage: React.FC<AdminProgramEnrollmentsPageProps> = ({ scope = 'all' }) => {
  const cfg = SCOPE_LABEL[scope];
  const snackbar = useSnackbar();
  const isMobile = useIsMobile();
  const [programs, setPrograms] = useState<ProgramLite[]>([]);
  const [programId, setProgramId] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [enrollments, setEnrollments] = useState<EnrollmentRow[]>([]);
  const [loadingPrograms, setLoadingPrograms] = useState(true);
  const [loadingEnrollments, setLoadingEnrollments] = useState(false);
  const [savingPayment, setSavingPayment] = useState<Set<string>>(new Set());
  const [savingStatus, setSavingStatus] = useState<Set<string>>(new Set());
  const [audienceFilter, setAudienceFilter] = useState<'all' | 'kids' | 'adult'>('all');
  const [error, setError] = useState<string | null>(null);
  // Move-to-another-program dialog
  const [transferFor, setTransferFor] = useState<EnrollmentRow | null>(null);
  const [transferTargetId, setTransferTargetId] = useState<string>('');
  const [transferBusy, setTransferBusy] = useState(false);

  function openTransfer(e: EnrollmentRow) {
    setTransferFor(e);
    setTransferTargetId('');
  }

  async function submitTransfer() {
    if (!transferFor || !transferTargetId) return;
    setTransferBusy(true);
    try {
      await swimService.transferEnrollment(transferFor.id, transferTargetId);
      const target = programs.find((p) => p.id === transferTargetId);
      const swimmerLabel = transferFor.enrollee
        ? `${transferFor.enrollee.first_name ?? ''} ${transferFor.enrollee.last_name ?? ''}`.trim() || 'swimmer'
        : 'swimmer';
      snackbar.success(
        `Moved ${swimmerLabel} to ${target?.name ?? 'new class'}`,
      );
      setTransferFor(null);
      // Refresh whatever view we're on. Single-program view: the moved
      // swimmer leaves the current list. All-programs: they show up under
      // the new program.
      if (programId === ALL_PROGRAMS) void loadAllEnrollments(statusFilter);
      else if (programId) void loadEnrollments(programId, statusFilter);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Transfer failed');
    } finally {
      setTransferBusy(false);
    }
  }

  async function updateStatus(enrollmentId: string, newStatus: string, swimmerLabel: string) {
    if (newStatus === 'cancelled') {
      const ok = window.confirm(
        `Cancel ${swimmerLabel}'s enrollment?\n\n` +
        `The swimmer will be removed from the active roster. If they hadn't paid yet, ` +
        `their payment status flips to "waived" automatically.`,
      );
      if (!ok) return;
    }
    setSavingStatus((s) => new Set(s).add(enrollmentId));
    try {
      const res = await axiosInstance.put<{ data: EnrollmentRow }>(
        `/swim-enrollments/${enrollmentId}/status`,
        { status: newStatus },
      );
      setEnrollments((rows) =>
        rows.map((r) =>
          r.id === enrollmentId
            ? {
                ...r,
                status: res.data.data.status,
                payment_status: res.data.data.payment_status,
                amount_paid: res.data.data.amount_paid,
                waitlist_position: res.data.data.waitlist_position,
              }
            : r,
        ),
      );
      snackbar.success(`Status changed to ${newStatus}`);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update status');
    } finally {
      setSavingStatus((s) => {
        const next = new Set(s);
        next.delete(enrollmentId);
        return next;
      });
    }
  }

  async function updatePaymentStatus(enrollmentId: string, newStatus: string) {
    setSavingPayment((s) => new Set(s).add(enrollmentId));
    try {
      const res = await axiosInstance.put<{ data: EnrollmentRow }>(
        `/swim-enrollments/${enrollmentId}/payment-status`,
        { payment_status: newStatus },
      );
      // Patch the local row from the server response so amount_paid stays
      // in sync with the side-effects applied server-side.
      setEnrollments((rows) =>
        rows.map((r) =>
          r.id === enrollmentId
            ? {
                ...r,
                payment_status: res.data.data.payment_status,
                amount_paid: res.data.data.amount_paid,
              }
            : r,
        ),
      );
      snackbar.success(`Payment marked ${newStatus}`);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      snackbar.error(err?.response?.data?.error || err?.message || 'Failed to update payment');
    } finally {
      setSavingPayment((s) => {
        const next = new Set(s);
        next.delete(enrollmentId);
        return next;
      });
    }
  }

  useEffect(() => {
    setProgramId(ALL_PROGRAMS);
    setEnrollments([]);
    void loadPrograms();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scope]);
  useEffect(() => {
    if (!programId) {
      setEnrollments([]);
      return;
    }
    if (programId === ALL_PROGRAMS) void loadAllEnrollments(statusFilter);
    else void loadEnrollments(programId, statusFilter);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [programId, statusFilter, audienceFilter]);

  async function loadPrograms() {
    setLoadingPrograms(true);
    setError(null);
    try {
      const res = await axiosInstance.get<{ data: ProgramLite[] }>('/programs/all', {
        params: { include_inactive: 'true' },
      });
      const allRows = res.data.data ?? [];
      const scoped = cfg.programTypes
        ? allRows.filter((p) => cfg.programTypes!.includes(String(p.program_type).toLowerCase()))
        : allRows;
      setPrograms(scoped);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load programs');
    } finally {
      setLoadingPrograms(false);
    }
  }

  async function loadAllEnrollments(status: string) {
    setLoadingEnrollments(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      // Scope the aggregated list to the page's program-type scope + the
      // current audience tab. 'all'/'all' returns everything that matches.
      if (cfg.programTypes && cfg.programTypes.length === 1) {
        params.program_type = cfg.programTypes[0];
      }
      if (showAudienceFilter && audienceFilter !== 'all') {
        params.target_audience = audienceFilter;
      }
      const res = await axiosInstance.get<{ data: EnrollmentRow[] }>(
        '/programs/all-enrollments',
        { params },
      );
      let rows = res.data.data ?? [];
      // 'tennis' scope covers tennis + pickleball — the backend only filters
      // on a single program_type. Apply the multi-type filter client-side.
      if (cfg.programTypes && cfg.programTypes.length > 1) {
        rows = rows.filter((r) =>
          cfg.programTypes!.includes(String(r.program?.program_type ?? '').toLowerCase()),
        );
      }
      setEnrollments(rows);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Failed to load enrollments');
    } finally {
      setLoadingEnrollments(false);
    }
  }

  async function loadEnrollments(id: string, status: string) {
    setLoadingEnrollments(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (status) params.status = status;
      const res = await axiosInstance.get<{ data: EnrollmentRow[] }>(
        `/programs/${id}/enrollments`,
        { params },
      );
      setEnrollments(res.data.data ?? []);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Failed to load enrollments');
    } finally {
      setLoadingEnrollments(false);
    }
  }

  const audienceOf = (p: ProgramLite) => (p.target_audience || 'kids').toLowerCase();

  // For swim scope we let admins toggle Kids / Adults so they don't have to
  // mentally separate Masters from Pre-Novice in the program dropdown.
  // Tennis/all scopes don't have the same audience model — hide the toggle.
  const showAudienceFilter = scope === 'swim';
  const visiblePrograms = useMemo(() => {
    if (!showAudienceFilter || audienceFilter === 'all') return programs;
    return programs.filter((p) => {
      const a = audienceOf(p);
      return a === audienceFilter || a === 'all';
    });
  }, [programs, audienceFilter, showAudienceFilter]);

  const kidsCount = useMemo(
    () => programs.filter((p) => ['kids', 'all'].includes(audienceOf(p))).length,
    [programs],
  );
  const adultCount = useMemo(
    () => programs.filter((p) => ['adult', 'all'].includes(audienceOf(p))).length,
    [programs],
  );

  // If the selected program is hidden by the audience filter, fall back
  // to "All programs" so the page still shows useful data.
  useEffect(() => {
    if (
      programId &&
      programId !== ALL_PROGRAMS &&
      !visiblePrograms.some((p) => p.id === programId)
    ) {
      setProgramId(ALL_PROGRAMS);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visiblePrograms]);

  const selectedProgram = useMemo(
    () => (programId && programId !== ALL_PROGRAMS ? programs.find((p) => p.id === programId) : undefined),
    [programs, programId],
  );
  const isAllProgramsMode = programId === ALL_PROGRAMS;

  // Tally for the toolbar
  const counts = useMemo(() => {
    const c = { registered: 0, waitlisted: 0, cancelled: 0, completed: 0, paid: 0, pending: 0 };
    for (const e of enrollments) {
      if (e.status in c) c[e.status as keyof typeof c] += 1;
      if (e.payment_status === 'paid') c.paid += 1;
      if (e.payment_status === 'pending') c.pending += 1;
    }
    return c;
  }, [enrollments]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        alignItems={{ xs: 'stretch', sm: 'center' }}
        justifyContent="space-between"
        spacing={{ xs: 1.5, sm: 0 }}
        sx={{ mb: 2 }}
      >
        <Stack direction="row" alignItems="center" spacing={2}>
          <GroupsIcon color="primary" fontSize="large" />
          <Box>
            <Typography variant="h4" component="h1">{cfg.title}</Typography>
            <Typography variant="body2" color="text.secondary">{cfg.subtitle}</Typography>
          </Box>
        </Stack>
        <IconButton
          onClick={() => { if (programId) void loadEnrollments(programId, statusFilter); }}
          disabled={loadingEnrollments || !programId}
        >
          <RefreshIcon />
        </IconButton>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {showAudienceFilter && (
        <ToggleButtonGroup
          size="small"
          value={audienceFilter}
          exclusive
          onChange={(_e, v) => v && setAudienceFilter(v)}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="all">All ({programs.length})</ToggleButton>
          <ToggleButton value="kids">Kids ({kidsCount})</ToggleButton>
          <ToggleButton value="adult">Adults ({adultCount})</ToggleButton>
        </ToggleButtonGroup>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl fullWidth disabled={loadingPrograms}>
          <InputLabel>Program</InputLabel>
          <Select
            value={programId}
            label="Program"
            onChange={(e) => setProgramId(e.target.value)}
          >
            <MenuItem value={ALL_PROGRAMS}>
              <strong>All programs</strong>
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                (flat list across {visiblePrograms.length} program{visiblePrograms.length === 1 ? '' : 's'})
              </Typography>
            </MenuItem>
            {visiblePrograms.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name} <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                  ({p.program_type}
                  {showAudienceFilter && ` • ${audienceOf(p)}`}
                  {' • '}{p.status})
                </Typography>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Status filter</InputLabel>
          <Select
            value={statusFilter}
            label="Status filter"
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Paper variant="outlined">
        <Toolbar sx={{ minHeight: '56px !important', flexWrap: 'wrap', gap: 1 }}>
          {(selectedProgram || isAllProgramsMode) && (
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexGrow: 1, flexWrap: 'wrap' }}>
              <Typography variant="subtitle1" sx={{ mr: 1 }}>
                {enrollments.length} enrollment{enrollments.length === 1 ? '' : 's'}
                {isAllProgramsMode && (
                  <Typography component="span" variant="body2" color="text.secondary" sx={{ ml: 0.5 }}>
                    across all programs
                  </Typography>
                )}
              </Typography>
              <Chip size="small" label={`${counts.registered} registered`} color="success" variant="outlined" />
              <Chip size="small" label={`${counts.waitlisted} waitlist`} color="warning" variant="outlined" />
              {counts.cancelled > 0 && <Chip size="small" label={`${counts.cancelled} cancelled`} variant="outlined" />}
              {counts.completed > 0 && <Chip size="small" label={`${counts.completed} completed`} color="info" variant="outlined" />}
              <Box sx={{ width: 16 }} />
              <Chip size="small" label={`${counts.paid} paid`} color="success" />
              <Chip size="small" label={`${counts.pending} pending`} color="warning" />
              {selectedProgram && selectedProgram.max_participants && (
                <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                  Capacity {counts.registered} / {selectedProgram.max_participants}
                </Typography>
              )}
            </Stack>
          )}
        </Toolbar>
        {loadingEnrollments ? (
          <Box display="flex" justifyContent="center" py={6}><CircularProgress /></Box>
        ) : !programId ? (
          <Box p={4} textAlign="center">
            <Typography color="text.secondary">Select a program above to see enrollments.</Typography>
          </Box>
        ) : (() => {
          const statusFgColor: Record<EnrollmentRow['status'], 'success.dark' | 'warning.dark' | 'info.dark' | 'text.secondary'> = {
            registered: 'success.dark',
            waitlisted: 'warning.dark',
            cancelled: 'text.secondary',
            completed: 'info.dark',
          };
          const renderStatusSelect = (e: EnrollmentRow) => {
            const saving = savingStatus.has(e.id);
            const label = swimmerName(e);
            return (
              <FormControl size="small" sx={{ minWidth: 130 }} disabled={saving}>
                <Select
                  value={e.status}
                  onChange={(ev) => void updateStatus(e.id, ev.target.value, label)}
                  sx={{
                    fontSize: '0.8125rem',
                    '& .MuiSelect-select': { py: 0.5 },
                    color: statusFgColor[e.status],
                    textDecoration: e.status === 'cancelled' ? 'line-through' : 'none',
                  }}
                >
                  <MenuItem value="registered">registered</MenuItem>
                  <MenuItem value="waitlisted">
                    waitlisted{e.waitlist_position != null && ` #${e.waitlist_position}`}
                  </MenuItem>
                  <MenuItem value="cancelled">cancelled</MenuItem>
                  <MenuItem value="completed">completed</MenuItem>
                </Select>
              </FormControl>
            );
          };
          const paymentColor: Record<EnrollmentRow['payment_status'], 'success.dark' | 'warning.dark' | 'info.dark' | 'text.secondary'> = {
            paid: 'success.dark',
            pending: 'warning.dark',
            refunded: 'info.dark',
            waived: 'text.secondary',
          };
          const renderPaymentSelect = (e: EnrollmentRow) => {
            const saving = savingPayment.has(e.id);
            return (
              <FormControl size="small" sx={{ minWidth: 110 }} disabled={saving}>
                <Select
                  value={e.payment_status}
                  onChange={(ev) => void updatePaymentStatus(e.id, ev.target.value)}
                  sx={{
                    fontSize: '0.8125rem',
                    '& .MuiSelect-select': { py: 0.5 },
                    color: paymentColor[e.payment_status],
                  }}
                >
                  <MenuItem value="pending">pending</MenuItem>
                  <MenuItem value="paid">paid</MenuItem>
                  <MenuItem value="refunded">refunded</MenuItem>
                  <MenuItem value="waived">waived</MenuItem>
                </Select>
              </FormControl>
            );
          };
          const swimmerName = (e: EnrollmentRow) =>
            e.enrollee ? `${e.enrollee.first_name ?? ''} ${e.enrollee.last_name ?? ''}`.trim() || '—' : '—';

          const columns: ResponsiveTableColumn<EnrollmentRow>[] = [
            {
              key: 'enrollee',
              label: 'Enrollee',
              renderCell: (e) => (
                <>
                  <Typography variant="body2"><strong>{swimmerName(e)}</strong></Typography>
                  {e.enrollee?.email && (
                    <Typography variant="caption" color="text.secondary">{e.enrollee.email}</Typography>
                  )}
                </>
              ),
            },
            ...(isAllProgramsMode
              ? [{
                  key: 'program',
                  label: 'Program',
                  renderCell: (e: EnrollmentRow) => (
                    <>
                      <Typography variant="body2">{e.program?.name ?? '—'}</Typography>
                      {e.program?.target_audience && (
                        <Typography variant="caption" color="text.secondary">
                          {e.program.target_audience}
                        </Typography>
                      )}
                    </>
                  ),
                } satisfies ResponsiveTableColumn<EnrollmentRow>]
              : []),
            {
              key: 'type',
              label: 'Type',
              renderCell: (e) => (
                <Typography variant="caption">{enrolleeBucket(e.enrollee?.type)}</Typography>
              ),
            },
            {
              key: 'age',
              label: 'Age',
              align: 'right',
              renderCell: (e) => {
                const age = ageFromDob(e.enrollee?.date_of_birth);
                return <Typography variant="caption">{age != null ? age : '—'}</Typography>;
              },
            },
            { key: 'status', label: 'Status', renderCell: renderStatusSelect },
            { key: 'payment', label: 'Payment', renderCell: renderPaymentSelect },
            {
              key: 'amount',
              label: 'Amount',
              align: 'right',
              renderCell: (e) => (
                <>
                  ${Number(e.amount_due).toFixed(2)}
                  {Number(e.amount_paid) > 0 && Number(e.amount_paid) !== Number(e.amount_due) && (
                    <Typography component="div" variant="caption" color="text.secondary">
                      paid ${Number(e.amount_paid).toFixed(2)}
                    </Typography>
                  )}
                </>
              ),
            },
            {
              key: 'enrolled',
              label: 'Enrolled',
              renderCell: (e) => (
                <Typography variant="caption">
                  {e.enrolled_at ? new Date(e.enrolled_at).toLocaleString() : '—'}
                </Typography>
              ),
            },
            {
              key: 'actions',
              label: 'Actions',
              align: 'right',
              renderCell: (e) => (
                <IconButton
                  size="small"
                  title="Move to another class"
                  onClick={() => openTransfer(e)}
                  disabled={e.status === 'cancelled'}
                >
                  <SwapHorizIcon fontSize="small" />
                </IconButton>
              ),
            },
          ];

          return (
            <ResponsiveTable<EnrollmentRow>
              rows={enrollments}
              columns={columns}
              rowKey={(e) => e.id}
              emptyState={
                <Typography color="text.secondary">
                  No enrollments {statusFilter && `with status "${statusFilter}" `}for this program.
                </Typography>
              }
              renderMobileCard={(e) => (
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="subtitle1"><strong>{swimmerName(e)}</strong></Typography>
                      {isAllProgramsMode && e.program && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {e.program.name}
                          {e.program.target_audience && ` • ${e.program.target_audience}`}
                        </Typography>
                      )}
                      {e.enrollee?.email && (
                        <Typography variant="caption" color="text.secondary">{e.enrollee.email}</Typography>
                      )}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {enrolleeBucket(e.enrollee?.type)}
                        {ageFromDob(e.enrollee?.date_of_birth) != null && ` • age ${ageFromDob(e.enrollee?.date_of_birth)}`}
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
                      <strong>${Number(e.amount_due).toFixed(2)}</strong>
                    </Typography>
                  </Stack>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
                    {renderStatusSelect(e)}
                    {renderPaymentSelect(e)}
                    <IconButton
                      size="small"
                      title="Move to another class"
                      onClick={() => openTransfer(e)}
                      disabled={e.status === 'cancelled'}
                    >
                      <SwapHorizIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    Enrolled {e.enrolled_at ? new Date(e.enrolled_at).toLocaleString() : '—'}
                  </Typography>
                </Stack>
              )}
            />
          );
        })()}
      </Paper>

      {/* Transfer dialog — admin moves a swimmer from this enrollment's
          current class to another. Backend cancels the old and creates a
          new enrollment in the target, copying the swimmer identity. */}
      <Dialog
        open={transferFor !== null}
        onClose={() => !transferBusy && setTransferFor(null)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Move{transferFor?.enrollee && ` ${transferFor.enrollee.first_name ?? ''} ${transferFor.enrollee.last_name ?? ''}`} to another class
          {transferFor?.program?.name && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
              Currently in {transferFor.program.name}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Current enrollment will be cancelled and a new one created in the target class.
              The swimmer's consent grants carry over automatically.
            </Typography>
            <FormControl fullWidth required>
              <InputLabel>Target class</InputLabel>
              <Select
                value={transferTargetId}
                label="Target class"
                onChange={(e) => setTransferTargetId(e.target.value)}
              >
                {programs
                  .filter((p) =>
                    p.is_active &&
                    p.status === 'published' &&
                    p.id !== (transferFor?.program?.id ?? '') &&
                    (showAudienceFilter
                      ? audienceFilter === 'all' ||
                        audienceOf(p) === audienceFilter ||
                        audienceOf(p) === 'all'
                      : true)
                  )
                  .map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.name}
                      <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                        ({p.program_type}{showAudienceFilter && ` • ${audienceOf(p)}`})
                      </Typography>
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: 'column-reverse', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > button': { width: { xs: '100%', sm: 'auto' }, ml: { xs: '0 !important', sm: undefined } },
          }}
        >
          <Button onClick={() => setTransferFor(null)} disabled={transferBusy}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void submitTransfer()}
            disabled={transferBusy || !transferTargetId}
          >
            {transferBusy ? 'Moving…' : 'Move swimmer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminProgramEnrollmentsPage;
